/**
 * Widget Dashboard « Encépagement » — branché sur `dashboard.stockParcellaire`
 * (Lot H.3). Répartition de la SURFACE EN PRODUCTION du vignoble par cépage
 * (hectares), avec pastille couleur authentique (référentiel cépages) + nom écrit
 * (couleur jamais seule) + jauge de part.
 *
 * NB : la maquette parlait de « vins clairs (hL) » mais l'endpoint expose bien
 * une SURFACE parcellaire (ha) — on affiche donc la réalité (ha), pas des hL
 * qu'on n'a pas (zéro chiffre inventé).
 *
 * Gating : masqué sans `parcellaire.read` (fail-closed).
 */

import { extractTrpcCode } from "@champ-erp/auth-primitives/web";
import type { StockCepage, StockParcellaireSortie } from "@champ-erp/contracts";
import { Card, ProgressBar, Skeleton, WidgetHeader, type ProgressTone } from "@champ-erp/ui";
import { useQuery } from "@tanstack/react-query";
import { Grape } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import { useCan } from "../../auth/use-can.js";
import { useCepages } from "../../components/forms/CepageSelect.js";
import { trpc } from "../../lib/trpc.js";
import { CepagePastille } from "../vignoble/CepagePastille.js";

/** Surface en hectares → « 4,2 ha » (2 décimales max, séparateur FR). */
function formatHectares(surfaceHa: number): string {
  return `${surfaceHa.toLocaleString("fr-FR", { maximumFractionDigits: 2 })} ha`;
}

/**
 * Tone de jauge par couleur de cépage — décoratif (l'identité du cépage est déjà
 * portée par la pastille + le nom). `rust` réservé aux warnings : on n'y touche pas.
 */
const COULEUR_TONE: Record<string, ProgressTone> = {
  blanc: "gold",
  noir: "aubergine",
  gris: "aubergine",
  rose: "vine",
};

export function WidgetStockParCepage() {
  const { granted, isLoading: permLoading } = useCan("parcellaire.read");
  const navigate = useNavigate();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["dashboard.stockParcellaire"],
    queryFn: () => trpc.dashboard.stockParcellaire.query(),
    enabled: granted,
    retry: false,
  });

  if (permLoading || !granted) return null;
  if (isError && extractTrpcCode(error) === "FORBIDDEN") return null;

  return (
    <Card as="section" className="p-6">
      <WidgetHeader
        eyebrow="Vignoble · Surface par cépage"
        title="Encépagement"
        action={{
          label: "Voir le vignoble",
          href: "/vignoble",
          onClick: (event) => {
            event.preventDefault();
            navigate("/vignoble");
          },
        }}
      />
      {isLoading ? (
        <Skeleton variant="text" lines={4} />
      ) : isError || !data ? (
        <p className="rounded-lg border border-dashed border-champagne-200 bg-champagne-50/40 px-4 py-6 text-center text-[13px] text-champagne-700">
          Impossible de charger l&apos;encépagement pour l&apos;instant.
        </p>
      ) : (
        <StockParCepageContenu stats={data} />
      )}
    </Card>
  );
}

function StockParCepageContenu({ stats }: { stats: StockParcellaireSortie }) {
  // Référentiel cépages (couleur + libellé), en cache : sert à colorer la pastille.
  const { data: cepagesRef } = useCepages();

  // État vide honnête : aucune surface en production → invitation.
  if (stats.cepages.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-6 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-champagne-50 text-champagne-700 ring-1 ring-champagne-200">
          <Grape size={26} />
        </div>
        <p className="text-[14px] text-champagne-800">
          Aucune parcelle en production pour l&apos;instant.
        </p>
        <Link
          to="/vignoble/nouvelle"
          className="text-[13px] font-medium text-champagne-700 underline-offset-2 hover:text-champagne-900 hover:underline"
        >
          Ajouter une parcelle →
        </Link>
      </div>
    );
  }

  // Index libellé (minuscule) → couleur du référentiel, pour retrouver la couleur
  // à partir du seul nom de cépage renvoyé par l'endpoint.
  const couleurParLibelle = new Map<string, string | null>();
  for (const cepage of cepagesRef ?? []) {
    couleurParLibelle.set(cepage.libelle.toLowerCase(), cepage.couleur);
  }

  return (
    <div className="space-y-4">
      {/* Total en complément de la ventilation (jamais seul). */}
      <p className="text-[13px] text-champagne-800">
        <span className="mono tnum font-semibold text-aubergine-900">
          {formatHectares(stats.surface_totale_ha)}
        </span>{" "}
        en production, répartis par cépage :
      </p>
      {stats.cepages.map((ligne) => (
        <CepageLigne
          key={ligne.cepage}
          ligne={ligne}
          couleur={couleurParLibelle.get(ligne.cepage.toLowerCase()) ?? null}
        />
      ))}
    </div>
  );
}

/** Une ligne cépage : pastille couleur + nom + surface + jauge de part. */
function CepageLigne({ ligne, couleur }: { ligne: StockCepage; couleur: string | null }) {
  const tone: ProgressTone = couleur ? (COULEUR_TONE[couleur] ?? "gold") : "gold";
  const part = Math.round(ligne.part_pourcentage);

  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <CepagePastille libelle={ligne.cepage} couleur={couleur} variant="inline" />
        <div className="mono tnum shrink-0 text-[14px] font-semibold text-aubergine-900">
          {formatHectares(ligne.surface_ha)}
          <span className="ml-1.5 text-[12px] font-normal text-champagne-700">{part} %</span>
        </div>
      </div>
      <ProgressBar
        value={part}
        tone={tone}
        label={`${ligne.cepage} : ${part} % de l'encépagement`}
        className="mt-2"
      />
    </div>
  );
}
